import type {
  MessageCreatedStreamEvent,
  ReasoningContentStreamEvent,
  TextMessageContentStreamEvent,
  ToolCallArgsStreamEvent,
  TurnReplaySSEFrame,
  TurnSSEFrame,
} from '../../client/streamEvents';
import type {TurnResponse, WbAgentClient} from '../../client/WbAgentClient';
import {TERMINAL_STATES} from '../../client/WbAgentClient';
import {GENERIC_RUN_ERROR_MESSAGE} from './errors';
import type {RuntimeEvent} from './events';
import {terminalMetadataEventsForTurn} from './turn';

const MAX_SSE_BUFFER_CHARS = 1_000_000;

// Allow one cursor replay after a dropped stream without risking an endless
// reconnect loop. Polling remains the correctness fallback after this.
const STREAM_RECONNECT_ATTEMPTS = 2;
const TERMINAL_TURN_FETCH_ATTEMPTS = 5;
const TERMINAL_TURN_FETCH_RETRY_MS = 250;

export interface StreamTurnRuntimeEventsOptions {
  includeSnapshot?: boolean;
  onSnapshot?: (turn: TurnResponse) => RuntimeEvent[];
  onTerminalSnapshot?: (turn: TurnResponse) => RuntimeEvent[];
}

export async function* streamTurnRuntimeEvents(
  client: WbAgentClient,
  turnId: string,
  runId: string,
  signal: AbortSignal,
  opts: StreamTurnRuntimeEventsOptions = {}
): AsyncGenerator<RuntimeEvent, void, undefined> {
  let after: number | undefined;
  let attempts = 0;
  let sawProgress = false;
  let includeSnapshot = opts.includeSnapshot ?? false;

  while (!signal.aborted && attempts < STREAM_RECONNECT_ATTEMPTS) {
    try {
      const response = await client.stream(
        `/turns/${turnId}/events`,
        streamParams(after, includeSnapshot),
        signal
      );
      includeSnapshot = false;
      const body = response.body;
      if (!body) {
        throw new Error('WbAgent API stream response had no body');
      }

      for await (const event of parseSSEStream(body, signal)) {
        const nextAfter = typeof event.id === 'number' ? event.id : after;
        sawProgress = true;
        if (event.type === 'turn-snapshot') {
          for (const runtimeEvent of opts.onSnapshot?.(event.payload.turn) ??
            []) {
            yield runtimeEvent;
            if (isRuntimeTerminalEvent(runtimeEvent)) {
              return;
            }
          }
          after = nextAfter;
          continue;
        }
        const runtimeEvents = streamEventToRuntimeEvents(event, runId);
        for (const runtimeEvent of runtimeEvents) {
          if (isRuntimeTerminalEvent(runtimeEvent)) {
            const {isTerminal, turn} = await fetchTerminalTurn(
              client,
              turnId,
              signal
            );
            if (!isTerminal) {
              throw new Error(
                'WbAgent stream terminal event arrived before terminal turn snapshot'
              );
            }
            for (const repairEvent of opts.onTerminalSnapshot?.(turn) ?? []) {
              yield repairEvent;
            }
            for (const metadataEvent of terminalMetadataEventsForTurn(turn)) {
              yield metadataEvent;
            }
          }
          yield runtimeEvent;
          if (isRuntimeTerminalEvent(runtimeEvent)) {
            return;
          }
        }
        after = nextAfter;
      }
      throw new Error('WbAgent stream closed before terminal event');
    } catch (err) {
      attempts++;
      if (signal.aborted || !sawProgress) {
        throw err;
      }
    }
  }

  throw new Error('WbAgent stream disconnected before completion');
}

function streamParams(
  after: number | undefined,
  includeSnapshot: boolean
): Record<string, unknown> | undefined {
  if (includeSnapshot) {
    return {include_snapshot: true};
  }
  return after == null ? undefined : {after};
}

function isRuntimeTerminalEvent(event: RuntimeEvent): boolean {
  return (
    event.type === 'run-finished' ||
    event.type === 'run-error' ||
    event.type === 'run-cancelled'
  );
}

async function* parseSSEStream(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal
): AsyncGenerator<TurnSSEFrame, void, undefined> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const {done, value} = await reader.read();
      if (done) {
        break;
      }
      buffer += decoder.decode(value, {stream: true}).replace(/\r\n/g, '\n');
      if (buffer.length > MAX_SSE_BUFFER_CHARS) {
        throw new Error('WbAgent stream frame exceeded maximum size');
      }
      let idx: number;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const rawEvent = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const parsed = parseSSEEvent(rawEvent);
        if (parsed) {
          yield parsed;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSSEEvent(rawEvent: string): TurnSSEFrame | null {
  let eventType = '';
  const dataLines: string[] = [];
  for (const line of rawEvent.split('\n')) {
    if (line.startsWith(':')) {
      continue;
    }
    if (line.startsWith('event:')) {
      eventType = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart());
    }
  }
  const data = dataLines.join('\n');
  if (!eventType || !data) {
    return null;
  }

  try {
    const envelope = JSON.parse(data) as TurnSSEFrame;
    // The generated type is compile-time only; the SSE header still guards the
    // discriminator before this object reaches the typed mapper below.
    if (envelope.type !== eventType) {
      return null;
    }
    return envelope;
  } catch {
    return null;
  }
}

async function fetchTerminalTurn(
  client: WbAgentClient,
  turnId: string,
  signal: AbortSignal
): Promise<{isTerminal: boolean; turn: TurnResponse}> {
  let lastTurn: TurnResponse | null = null;
  for (let attempt = 0; attempt < TERMINAL_TURN_FETCH_ATTEMPTS; attempt++) {
    const turn = await client.get<TurnResponse>(
      `/turns/${turnId}`,
      undefined,
      signal
    );
    lastTurn = turn;
    if (TERMINAL_STATES.includes(turn.state)) {
      return {isTerminal: true, turn};
    }
    if (attempt < TERMINAL_TURN_FETCH_ATTEMPTS - 1) {
      await sleep(TERMINAL_TURN_FETCH_RETRY_MS, signal);
    }
  }

  return {isTerminal: false, turn: lastTurn as TurnResponse};
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      {once: true}
    );
  });
}

function streamEventToRuntimeEvents(
  event: TurnReplaySSEFrame,
  runId: string
): RuntimeEvent[] {
  // Translate WBAgent SSE/OpenAPI wire events into RuntimeEvent shapes. This
  // is the boundary where backend replay/progress events become the UI event
  // model consumed by the chat store and assistant-ui, so replay-specific
  // transport details do not leak deeper into the frontend.
  switch (event.type) {
    case 'run-started':
      // The frontend creates the UI run before opening SSE. Backend run ids are
      // turn ids, so this wire lifecycle event is intentionally not re-emitted.
      return [];
    case 'message-created': {
      const {payload} = event as MessageCreatedStreamEvent;
      if (
        (payload.role !== 'assistant' && payload.role !== 'reasoning') ||
        payload.content == null
      ) {
        return [];
      }
      if (payload.role === 'reasoning') {
        return [
          {type: 'reasoning-start', messageId: payload.messageId},
          {
            type: 'reasoning-content',
            messageId: payload.messageId,
            delta: payload.content,
          },
          {type: 'reasoning-end', messageId: payload.messageId},
        ];
      }
      return [
        {type: 'text-message-start', messageId: payload.messageId},
        {
          type: 'text-message-content',
          messageId: payload.messageId,
          delta: payload.content,
        },
        {type: 'text-message-end', messageId: payload.messageId},
      ];
    }
    case 'text-message-start':
    case 'text-message-end':
    case 'reasoning-start':
    case 'reasoning-end': {
      const {payload} = event as Extract<
        TurnReplaySSEFrame,
        | {type: 'text-message-start'}
        | {type: 'text-message-end'}
        | {type: 'reasoning-start'}
        | {type: 'reasoning-end'}
      >;
      return [{type: event.type, messageId: payload.messageId} as RuntimeEvent];
    }
    case 'text-message-content':
    case 'reasoning-content': {
      const {payload} = event as
        | TextMessageContentStreamEvent
        | ReasoningContentStreamEvent;
      return [
        {
          type: event.type,
          messageId: payload.messageId,
          delta: payload.delta,
        } as RuntimeEvent,
      ];
    }
    case 'tool-call-start': {
      const {payload} = event as Extract<
        TurnReplaySSEFrame,
        {type: 'tool-call-start'}
      >;
      if (!payload.messageId || !payload.toolCallId || !payload.toolName) {
        return [];
      }
      return [{...payload, type: event.type} as RuntimeEvent];
    }
    case 'tool-call-args': {
      const {payload} = event as ToolCallArgsStreamEvent;
      if (!payload.toolCallId || payload.delta == null) {
        return [];
      }
      return [{...payload, type: event.type} as RuntimeEvent];
    }
    case 'tool-call-end': {
      const {payload} = event as Extract<
        TurnReplaySSEFrame,
        {type: 'tool-call-end'}
      >;
      if (!payload.toolCallId) {
        return [];
      }
      return [{...payload, type: event.type} as RuntimeEvent];
    }
    case 'tool-call-result': {
      const {payload} = event as Extract<
        TurnReplaySSEFrame,
        {type: 'tool-call-result'}
      >;
      if (
        !payload.toolCallId ||
        payload.content == null ||
        typeof payload.isError !== 'boolean'
      ) {
        return [];
      }
      return [{...payload, type: event.type} as RuntimeEvent];
    }
    case 'run-finished':
      return [{type: 'run-finished', runId}];
    case 'run-cancelled':
      return [{type: 'run-cancelled', runId}];
    case 'run-error':
      // The frame's message is operator detail, never shown to the user.
      return [{type: 'run-error', runId, message: GENERIC_RUN_ERROR_MESSAGE}];
    default:
      return assertNever(event);
  }
}

function assertNever(_value: never): never {
  throw new Error('Unhandled WbAgent stream event type');
}
